import { useCallback, useMemo, useState } from "react";
import { useStores } from "stores/setup/use-store";
import * as Yup from "yup";
import { EditAlertFormValues } from "pages/Alert/types";
import { useHistory, useLocation } from "react-router-dom";
import { conditionGroupsSchema } from "pages/Alert/util/conditionGroupsSchema";
import {
  DEFAULT_MIN_ADVANCE_TIME,
  DEFAULT_MAX_ADVANCE_TIME,
} from "../constants/SendConfigConstants";

export function useAlertEditFormData() {
  const {
    dataStores: { insightAlertStore },
  } = useStores();
  const history = useHistory();
  const location = useLocation();

  const [initialValues, setInitialValues] = useState<EditAlertFormValues>({
    name: "",
    alertType: "",
    channels: ["whatsapp"],
    selectedUsers: [],
    customTitle: "",
    customDescription: "",
    minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
    maxAdvanceTime: DEFAULT_MAX_ADVANCE_TIME,
    minimumEventDuration: 60,
    receiptType: ["sendUpdates", "sendCancellations"],
    startTime: "00:00",
    endTime: "23:59",
    macroRegionId: undefined,
    stationRegion: undefined,
    // Insight alert fields
    insightId: undefined,
    dashboardId: undefined,
  });

  // Check if editing an insight alert (has operationRuleLocationItemId)
  const isInsightAlert = !!insightAlertStore.operationRuleAlertForTableForEdit?.operationRuleLocationItemId;

  const validationSchema = useMemo(() => Yup.object()
    .shape({
      name: Yup.string().required("name is required"),
      customTitle: Yup.string().required("customTitle is required"),
      customDescription: Yup.string().required("customDescription is required"),
      channels: Yup.array().min(1, "channels is required"),
      // Only validate conditionGroups for standalone alerts, not insight alerts
      conditionGroups: isInsightAlert ? Yup.array().notRequired() : conditionGroupsSchema,
      sensorAlertConfig: Yup.object().nullable().when({
        is: (val: any) => val && val.sensors && val.sensors.length > 0,
        then: () => Yup.object().shape({
          conditions: conditionGroupsSchema,
        }),
        otherwise: () => Yup.object().nullable(),
      }),
    })
    .test(
      "location-validation",
      "Please provide location information",
      function (values: any) {
        // For insight alerts, location comes from the insight, so skip this validation
        if (isInsightAlert) {
          return true;
        }

        const hasAreaLocation =
          (values.geofence && values.geofence.length > 0) || values.locationId;

        const hasPointLocation = values.macroRegionId && values.stationRegion;

        if (!hasAreaLocation && !hasPointLocation) {
          return this.createError({
            path: "geofence",
            message: "Please provide location information",
          });
        }

        return true;
      }
    ), [isInsightAlert]);

  const resetInitialValues = useCallback(() => {
    insightAlertStore.setOperationRuleAlert(null);
    insightAlertStore.setSelectedGeofence([]);
    insightAlertStore.setAreaSelectorVisible(false);
    insightAlertStore.setAlertLocationType(null);
    insightAlertStore.setSelectedStationForAlert(null);
    insightAlertStore.setOriginalStationForAlert(null);
    insightAlertStore.setMacroRegionOrigin(null);
    insightAlertStore.setIsGeofenceInvalid(false);
    insightAlertStore.setIsGeofenceSavedToLocation(false);
    history.replace(location.pathname);

    setInitialValues({
      name: "",
      alertType: "",
      channels: ["whatsapp"],
      selectedUsers: [],
      customTitle: "",
      customDescription: "",
      minAdvanceTime: DEFAULT_MIN_ADVANCE_TIME,
      maxAdvanceTime: DEFAULT_MAX_ADVANCE_TIME,
      minimumEventDuration: 60,
      receiptType: ["sendUpdates", "sendCancellations"],
      startTime: "00:00",
      endTime: "23:59",
      macroRegionId: undefined,
      stationRegion: undefined,
      // Clear insight alert fields to prevent stale data
      insightId: undefined,
      dashboardId: undefined,
    });
  }, [insightAlertStore, history, location.pathname]);

  const updateFormValues = useCallback((values: EditAlertFormValues) => {
    setInitialValues(values);
  }, []);

  const buildReceiptArray = useCallback(
    (sendUpdates?: boolean, sendCancellations?: boolean): string[] => {
      const receipt: string[] = [];

      if (sendUpdates) {
        receipt.push("sendUpdates");
      }

      if (sendCancellations) {
        receipt.push("sendCancellations");
      }

      return receipt;
    },
    []
  );

  return {
    initialValues,
    validationSchema,
    resetInitialValues,
    updateFormValues,
    buildReceiptArray,
  };
}
